<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              <i class="fas fa-tag me-2"></i>{{ t("receipts.discount_screen_title") }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3 position-relative">
        <div class="row justify-content-center">
          <div class="col-md-10 ms-3">
            <div class="row g-3">
              <!-- Guardian selector -->
              <div class="col-md-6">
                <label class="bmd-label-float">{{ t("receipts.guardian_label") }}</label>
                <input
                  type="text"
                  v-model="guardianSearch"
                  class="form-control borderless-input"
                  :placeholder="t('receipts.guardian_placeholder')"
                  @input="filterGuardians"
                />
                <div
                  v-if="filteredGuardians.length > 0 && guardianSearch"
                  class="list-group guardian-search-results"
                >
                  <a
                    v-for="g in filteredGuardians"
                    :key="g.id"
                    href="#"
                    class="list-group-item list-group-item-action"
                    @click.prevent="selectGuardian(g)"
                  >
                    {{ g.username }} - {{ g.phone_num }}
                    <span v-if="g.is_primary" class="badge bg-success ms-2">{{ t("receipts.primary") }}</span>
                  </a>
                </div>
              </div>

              <!-- Academic year -->
              <div class="col-md-6">
                <label class="bmd-label-float">{{ t("receipts.academic_year") }}</label>
                <select
                  v-model="academicYearId"
                  class="form-select borderless-input"
                >
                  <option value="" disabled>{{ t("receipts.select_academic_year") }}</option>
                  <option v-for="year in academicYears" :value="year.id" :key="year.id">
                    {{ year.name }} ({{ year.year }})<span v-if="year.is_active"> - {{ t("common.current") }}</span>
                  </option>
                </select>
              </div>

              <!-- Loading -->
              <div v-if="loadingChildren" class="col-12 text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x text-secondary"></i>
              </div>

              <!-- No children with remaining balance -->
              <div
                v-else-if="guardianId && academicYearId && children.length === 0"
                class="col-12"
              >
                <div class="alert alert-info mb-0">
                  <i class="fas fa-info-circle me-2"></i>{{ t("receipts.no_children_found") }}
                </div>
              </div>

              <!-- Children table -->
              <div v-if="children.length > 0" class="col-12">
                <div class="card bg-light mb-2">
                  <div class="card-body p-2">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0"><i class="fas fa-children me-2"></i>{{ t("receipts.select_children") }}</h6>
                      <button type="button" class="btn btn-sm btn-outline-secondary" @click="toggleSelectAll">
                        {{ allSelected ? t("receipts.deselect_all") : t("receipts.select_all") }}
                      </button>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0 align-middle">
                        <thead>
                          <tr>
                            <th style="width: 40px"></th>
                            <th>{{ t("receipts.student") }}</th>
                            <th>{{ t("receipts.class_fee") }}</th>
                            <th>{{ t("receipts.total_paid") }}</th>
                            <th>{{ t("receipts.current_balance") }}</th>
                            <th class="text-danger">{{ t("receipts.discount_value") }}</th>
                            <th class="text-success">{{ t("receipts.remaining_after") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="child in childrenDisplay"
                            :key="child.student_id"
                            :class="child.remaining <= 0 ? 'text-muted' : ''"
                          >
                            <td>
                              <input
                                type="checkbox"
                                class="form-check-input"
                                v-model="child.selected"
                                :disabled="child.remaining <= 0"
                              />
                            </td>
                            <td>{{ child.student_name }}</td>
                            <td>{{ formatCurrency(child.class_fee) }}</td>
                            <td>{{ formatCurrency(child.paid) }}</td>
                            <td>{{ formatCurrency(child.remaining) }}</td>
                            <td class="text-danger">
                              {{ child.selected && child.discount_preview > 0 ? '-' + formatCurrency(child.discount_preview) : formatCurrency(0) }}
                            </td>
                            <td class="fw-bold" :class="child.selected ? 'text-success' : ''">
                              {{ formatCurrency(child.remaining_after) }}
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr class="fw-bold border-top">
                            <td colspan="4"></td>
                            <td>{{ formatCurrency(totalSelectedRemaining) }}</td>
                            <td class="text-danger">-{{ formatCurrency(totalDiscountPreview) }}</td>
                            <td class="text-success">{{ formatCurrency(totalSelectedRemaining - totalDiscountPreview) }}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Discount percentage -->
              <div class="col-md-6" v-if="children.length > 0">
                <label class="bmd-label-float">{{ t("receipts.discount_percentage_label") }}</label>
                <div class="input-group">
                  <input
                    type="number"
                    v-model.number="discountPercentage"
                    class="form-control borderless-input"
                    min="0.01"
                    max="100"
                    step="0.5"
                    :placeholder="t('receipts.discount_percentage_placeholder')"
                  />
                  <span class="input-group-text">%</span>
                </div>
              </div>

              <!-- Notes -->
              <div class="col-12" v-if="children.length > 0">
                <label class="bmd-label-float">{{ t("receipts.notes") }}</label>
                <textarea
                  v-model="notes"
                  class="form-control borderless-input"
                  rows="2"
                  :placeholder="t('receipts.notes_placeholder')"
                ></textarea>
              </div>

              <div v-if="submitError" class="col-12">
                <div class="alert alert-danger mb-0">{{ submitError }}</div>
              </div>

              <!-- Submit -->
              <div class="col-12 mt-4" v-if="children.length > 0">
                <div class="d-flex gap-3">
                  <button
                    type="button"
                    class="btn btn-primary flex-grow-1"
                    :disabled="isSubmitting || !canSubmit"
                    @click="submitDiscount"
                  >
                    <span v-if="isSubmitting">
                      <i class="fas fa-spinner fa-spin me-2"></i>{{ t("receipts.saving") }}
                    </span>
                    <span v-else>
                      <i class="fas fa-tag ms-2"></i>{{ t("receipts.apply_bulk_discount") }}
                    </span>
                  </button>
                  <router-link :to="{ name: 'payment-receipts.index' }" class="btn btn-outline-secondary flex-grow-1">
                    <i class="fas fa-times ms-2"></i>{{ t("receipts.cancel") }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";

const { t, locale } = useI18n();
const toast = useToast();
const router = useRouter();

const guardians = ref([]);
const filteredGuardians = ref([]);
const guardianSearch = ref("");
const guardianId = ref("");

const academicYears = ref([]);
const academicYearId = ref("");

const children = ref([]); // [{ student_id, student_name, class_fee, paid, remaining, selected }]
const loadingChildren = ref(false);

const discountPercentage = ref(null);
const notes = ref("");

const isSubmitting = ref(false);
const submitError = ref("");

function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t("auth.unauthorized", "Unauthorized"));
  if (s === 403) return toast.error(t("auth.forbidden", "Forbidden"));
  return toast.error(extractApiMessage(error, fallbackKey));
}

const loadAcademicYears = async () => {
  try {
    const response = await api.get("/academic-years");
    academicYears.value = response.data.data || [];
    const currentYear = academicYears.value.find((y) => y.is_active);
    if (currentYear) {
      academicYearId.value = currentYear.id;
    }
  } catch (error) {
    console.error("Error loading academic years:", error);
  }
};

const fetchGuardians = async () => {
  try {
    const response = await api.get("/guardians/primary/list");
    guardians.value = response.data.data;
  } catch (error) {
    notifyError(error, "receipts.guardians_failed");
  }
};

const filterGuardians = () => {
  if (!guardianSearch.value) {
    filteredGuardians.value = [];
    return;
  }
  const searchTerm = guardianSearch.value.toLowerCase();
  filteredGuardians.value = guardians.value.filter(
    (g) =>
      g.username.toLowerCase().includes(searchTerm) ||
      g.phone_num.includes(searchTerm)
  );
};

const selectGuardian = (guardian) => {
  guardianId.value = guardian.id;
  guardianSearch.value = `${guardian.username} - ${guardian.phone_num}`;
  filteredGuardians.value = [];
};

// Only students with a positive remaining balance for the chosen year are
// worth showing here — a discount on a student who owes nothing has
// nothing to do.
const loadChildren = async () => {
  submitError.value = "";
  if (!guardianId.value || !academicYearId.value) {
    children.value = [];
    return;
  }
  loadingChildren.value = true;
  try {
    const studentsRes = await api.get(`/guardians/${guardianId.value}/students`);
    const kids = studentsRes.data.data || [];

    const statuses = await Promise.all(
      kids.map((k) =>
        api
          .get(`/payment-receipts/student-fee-status/${k.id}`, {
            params: { academic_year_id: academicYearId.value },
          })
          .then((r) => ({
            student_id: k.id,
            student_name: `${k.first_name} ${k.father_name} ${k.last_name}`,
            class_fee: Number(r.data.class_fee || 0),
            paid: Number(r.data.paid || 0),
            remaining: Math.max(0, Number(r.data.remaining || 0)),
            selected: false,
          }))
      )
    );

    // Show every child, but only ones with a remaining balance can be
    // selected/checked (see :disabled on the checkbox).
    children.value = statuses;
  } catch (e) {
    console.error("Error loading children:", e);
    children.value = [];
    notifyError(e, "receipts.data_failed");
  } finally {
    loadingChildren.value = false;
  }
};

const allSelected = computed(() => {
  const selectable = children.value.filter((c) => c.remaining > 0);
  return selectable.length > 0 && selectable.every((c) => c.selected);
});

const toggleSelectAll = () => {
  const target = !allSelected.value;
  children.value = children.value.map((c) => ({
    ...c,
    selected: c.remaining > 0 ? target : c.selected,
  }));
};

// Each child's discount is computed independently from their own remaining
// balance — never from a shared pool — so one sibling's discount can never
// spill onto another's.
const childrenDisplay = computed(() => {
  const pct = Number(discountPercentage.value) || 0;
  return children.value.map((c) => {
    const preview =
      c.selected && pct > 0 ? Math.round(c.remaining * pct) / 100 : 0;
    return {
      ...c,
      discount_preview: preview,
      remaining_after: Math.max(0, Math.round((c.remaining - preview) * 100) / 100),
    };
  });
});

const totalSelectedRemaining = computed(() =>
  childrenDisplay.value
    .filter((c) => c.selected)
    .reduce((s, c) => s + c.remaining, 0)
);

const totalDiscountPreview = computed(() =>
  childrenDisplay.value.reduce((s, c) => s + c.discount_preview, 0)
);

const canSubmit = computed(() => {
  const pct = Number(discountPercentage.value) || 0;
  return (
    guardianId.value &&
    academicYearId.value &&
    pct > 0 &&
    pct <= 100 &&
    children.value.some((c) => c.selected && c.remaining > 0)
  );
});

const submitDiscount = async () => {
  if (!canSubmit.value) return;
  isSubmitting.value = true;
  submitError.value = "";
  try {
    const studentIds = children.value
      .filter((c) => c.selected && c.remaining > 0)
      .map((c) => c.student_id);

    const res = await api.post("/payment-receipts/bulk-discount", {
      guardian_id: guardianId.value,
      academic_year_id: academicYearId.value,
      discount_percentage: Number(discountPercentage.value),
      student_ids: studentIds,
      notes: notes.value || null,
    });

    const applied = res.data?.data?.applied || [];
    const skipped = res.data?.data?.skipped || [];

    toast.success(
      t("receipts.bulk_discount_applied_count", { count: applied.length })
    );
    if (skipped.length > 0) {
      toast.warning(
        t("receipts.bulk_discount_skipped_count", { count: skipped.length })
      );
    }

    router.push({ name: "payment-receipts.index" });
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t("auth.forbidden", "Forbidden"));
    } else {
      const msg = extractApiMessage(error, "receipts.save_failed");
      submitError.value = msg;
      toast.error(msg);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "0 " + t("receipts.lyd_abbr");
  }
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) {
    return "0 " + t("receipts.lyd_abbr");
  }
  return Math.round(numericAmount).toLocaleString() + " " + t("receipts.lyd_abbr");
};

// Guardian and academic year can each finish loading independently (the
// guardian is picked by the user, the year is auto-selected once its API
// call resolves) — watching both, rather than relying only on the select's
// @change, means the table still loads if the year finishes resolving
// after the guardian was already picked.
watch([guardianId, academicYearId], () => {
  loadChildren();
});

fetchGuardians();
loadAcademicYears();
</script>

<style scoped>
.guardian-search-results {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}
.guardian-search-results a {
  cursor: pointer;
}
.guardian-search-results a:hover {
  background-color: #f5f5f5;
}
</style>
